import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Profile.css';
import 'react-phone-number-input/style.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useProfileAccountActions } from './profile.logic';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import { useProfileForm } from './hooks';
import ProfileTabs from './ProfileTabs';
import ProfileStep1 from './steps/ProfileStep1';
import ProfileStep2 from './steps/ProfileStep2';
import ProfileStep3 from './steps/ProfileStep3';

const Profile: React.FC = () => {
	const { t } = useTranslation();
	const [activeTab, setActiveTab] = useState(0);
	const { personalInformationPost, isLoading } = useProfileAccountActions();
	const {
		formData,
		avatar,
		notification,
		notificationsEnabled,
		errors,
		updateFormField,
		setAvatar,
		setNotification,
		setNotificationsEnabled,
		validateStep1,
		getProfileState,
	} = useProfileForm();

	const handleTabClick = (index: number) => {
		setActiveTab(index);
	};

	const handleContinueClick = () => {
		const state = getProfileState();
		personalInformationPost({
			name: state.formData.nameValue,
			surname: state.formData.lastNameValue,
			companyPosition: state.formData.occupationValue,
			companyName: state.formData.companyValue,
			country: state.formData.country,
			countryCode: state.formData.countryCode,
			mobileNumber: state.formData.mobileNumber,
			avatar: state.avatar,
			notificationEnabled: state.notificationsEnabled,
			notifications: state.notification,
		});
	};

	const handleNext = () => {
		if (activeTab === 0) {
			// Validate step 1 before proceeding
			if (validateStep1()) {
				setActiveTab(1);
			}
		} else if (activeTab === 1) {
			// Move to step 3 (no validation needed for avatar)
			setActiveTab(2);
		} else if (activeTab === 2) {
			// Submit the form on step 3
			handleContinueClick();
		}
	};

	const getStepTitle = () => {
		switch (activeTab) {
			case 0:
				return t('register.testProfile.step_1.title');
			case 1:
				return t('register.testProfile.step_2.title');
			case 2:
				return t('register.testProfile.step_3.title');
			default:
				return '';
		}
	};

	const handleNextClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		handleNext();
	};

	const renderStepContent = () => {
		switch (activeTab) {
			case 0:
				return (
					<ProfileStep1
						formData={formData}
						errors={errors}
						onFieldChange={updateFormField}
					/>
				);
			case 1:
				return (
					<ProfileStep2
						selectedAvatar={avatar}
						onAvatarSelect={setAvatar}
					/>
				);
			case 2:
				return (
					<ProfileStep3
						selectedNotification={notification}
						notificationsEnabled={notificationsEnabled}
						onNotificationSelect={setNotification}
						onNotificationsEnabledChange={setNotificationsEnabled}
					/>
				);
			default:
				return null;
		}
	};

	if (isLoading) {
		return <CircularLoading />;
	}

	return (
		<div className="container container-test-profile mt-5">
			<div className="row">
				<ProfileTabs
					activeTab={activeTab}
					onTabClick={handleTabClick}
				/>
				<div className="tab-container">
					<div className="titleRow">{getStepTitle()}</div>
					{renderStepContent()}
				</div>
				<div className="btn-footer">
					<div className="btn-test-next">
						<button
							className="btn-test-next-text"
							type="button"
							onClick={handleNextClick}>
							{t('register.testProfile.footer.save')}
							<FontAwesomeIcon
								icon={faArrowRight}
								color="black"
							/>
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Profile;
